import React, { useEffect, useMemo } from "react";
import type { ReactNode } from "react";
import { BrowserRouter } from "react-router-dom";
import { entities } from "byzantine";
import Transaction from "byzantine/src/Transaction";
import type Account from "byzantine/src/Account";
import type { TransactionResponse } from "byzantine/src/Transaction";
import { useBreakpoints } from "@narmi/design_system";
import PageLayout from "../../src/components/PageLayout";
import HeroBanner from "../HeroBanner";
import HoldsCard from "./HoldsCard";
import TransactionsCard from "./TransactionsCard";
import ScheduledTransfersCard from "./ScheduledTransfersCard";
import AccountDetailsCard from "./AccountDetailsCard";
import BalancesCard from "./BalancesCard";
import StatementsCard from "./StatementsCard";
import ServicesAndRequests from "./ServicesAndRequests";
import PendingWiresCard from "./PendingWiresCard";
import { AccountContextProvider } from "../contexts/AccountContext";
import { InstitutionSettingsContextProvider } from "../contexts/InstitutionSettingsContext";
import AccountBanner from "./AccountBanner";
import LoanPaymentBanner from "./LoanPaymentBanner";
import LoanAutopayCard from "./LoanAutopayCard";

type ProviderWrapperProps = {
  children?: ReactNode;
  accounts: Account[];
};

type AccountPageContainerProps = {
  accounts: Account[];
  accountUuid?: string;
  transactionsJson?: string;
  limitsJson?: string;
  replyTime: string;
};

const ProviderWrapper = ({ children, accounts }: ProviderWrapperProps) => (
  <AccountContextProvider accounts={accounts}>
    <InstitutionSettingsContextProvider>
      {children}
    </InstitutionSettingsContextProvider>
  </AccountContextProvider>
);

const AccountPageContainer = ({
  accounts,
  accountUuid = "",
  transactionsJson,
  limitsJson,
  replyTime,
}: AccountPageContainerProps) => {
  const { send: fetchRecipients, loading: loadingRecipients } =
    entities.recipients.useFetchRecipients();
  const { l } = useBreakpoints();
  const recipients = entities.recipients.useRecipients();
  const account = accounts.find((a) => a.id === accountUuid);

  const limits: API.Limits.DisplayLimits = useMemo(() => {
    try {
      return JSON.parse(limitsJson!);
    } catch {
      return {};
    }
  }, [limitsJson]);

  const transactions = useMemo(() => {
    try {
      const parsedTransactions: TransactionResponse[] = JSON.parse(
        transactionsJson!
      );
      return parsedTransactions.map((t) =>
        Transaction.deserialize(t, account!)
      );
    } catch {
      return [];
    }
  }, [transactionsJson, account]);

  useEffect(() => {
    fetchRecipients();
  }, []);

  if (!accountUuid || loadingRecipients || !account) {
    return null;
  }

  return (
    <BrowserRouter basename="/">
      <ProviderWrapper accounts={accounts}>
        <PageLayout
          layout="dual-column"
          columnRatio="2fr 1fr"
          header={
            <>
              <AccountBanner account={account} />
              <LoanPaymentBanner account={account} />
              <HeroBanner account={account} />
            </>
          }
          maxWidth="l"
        >
          {/* Left column - main content */}
          <div>
            {!l && <BalancesCard account={account} />}
            {!l && (
              <div style={{ paddingTop: "var(--space-l)" }}>
                <LoanAutopayCard account={account} />
              </div>
            )}
            {!l && <AccountDetailsCard account={account} />}
            <ScheduledTransfersCard
              accountUuid={account.id}
              limits={limits}
              recipients={recipients}
            />
            <PendingWiresCard accountUuid={account.id} />
            <HoldsCard accountUuid={account.id} />
            <TransactionsCard
              account={account}
              recentTransactions={transactions}
              replyTime={replyTime}
            />
          </div>

          {/* Right column - sidebar cards */}
          <div>
            {l && <BalancesCard account={account} />}
            {l && <LoanAutopayCard account={account} />}
            {l && <AccountDetailsCard account={account} />}
            <StatementsCard />
            <ServicesAndRequests account={account} />
          </div>
        </PageLayout>
      </ProviderWrapper>
    </BrowserRouter>
  );
};

export default AccountPageContainer;
